import * as rrd from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import { oauth } from '@/lib';
import { useAuth } from '@/hooks';
import { OAuthContext } from '@/contexts';

const { apple, spotify, youtube } = oauth;

const { useLocation } = rrd.default ?? rrd;

const initialPlatformState = {
  error: null,
  userName: '',
  accountId: '',
  expiresAt: null,
  accessToken: null,
  isSignedIn: false,
  isSigningIn: false,
  isSigningOut: false,
};

const roles = ['source', 'target'];

const OAuthProvider = ({ children }) => {
  const { pathname } = useLocation();

  const { onUnauthorized } = useAuth();

  const [accounts, setAccounts] = useState({
    apple: [],
    spotify: [],
    youtube: [],
  });

  const [authState, setAuthState] = useState({
    source: {
      apple: { ...initialPlatformState },
      spotify: { ...initialPlatformState },
      youtube: { ...initialPlatformState },
    },
    target: {
      apple: { ...initialPlatformState },
      spotify: { ...initialPlatformState },
      youtube: { ...initialPlatformState },
    },
  });

  const updateAuthState = (role, platform, patch) => {
    const { accounts, ...rest } = patch;

    if (accounts) {
      setAccounts((prev) => ({
        ...prev,
        [platform]: accounts,
      }));
    }

    if (rest) {
      setAuthState((prev) => ({
        ...prev,
        [role]: {
          ...prev[role],
          [platform]: { ...prev[role][platform], ...rest },
        },
      }));
    }
  };

  useEffect(() => {
    // Determine the role priority based on the current transfer type.
    // If we are on the 'Account' tab, 'source' is primary.
    // Otherwise (Single/Profile tabs), 'target' is primary.
    const isAccountTab = pathname.endsWith('/account');
    const primaryRole = isAccountTab ? 'source' : 'target';
    const secondaryRole = isAccountTab ? 'target' : 'source';

    let nextState = { ...authState };
    let hasChanges = false;

    Object.keys(accounts).forEach((platform) => {
      const platformAccounts = accounts[platform] || [];

      if (platformAccounts.length === 0) {
        roles.forEach((role) => {
          // Only trigger a change if the current state actually contains data for this platform
          if (nextState[role][platform]?.accountId) {
            hasChanges = true;
            nextState = {
              ...nextState,
              [role]: {
                ...nextState[role],
                [platform]: { ...initialPlatformState },
              },
            };
          }
        });

        // Skip the rest of the logic for this platform
        return;
      }

      // Sort to find the most recently linked account
      const newestAccount = [...platformAccounts].sort(
        (a, b) => new Date(b.connectedAt || 0) - new Date(a.connectedAt || 0)
      )[0];

      roles.forEach((role) => {
        const currentAccount = nextState[role][platform];
        const currentId = currentAccount?.accountId;
        const currentExists = platformAccounts.some(
          (acc) => acc.accountId === currentId
        );

        let targetAccount;

        // If only one account exists, it must be used for both source and target.
        if (platformAccounts.length === 1) {
          targetAccount = platformAccounts[0];
        } else {
          const isPrimary = role === primaryRole;
          const otherRole = isPrimary ? secondaryRole : primaryRole;
          const otherId = nextState[otherRole][platform]?.accountId;

          // If we are on a Single/Profile transfer tab, force the Target (primaryRole)
          // to be the most recently added account.
          if (!isAccountTab && isPrimary) {
            targetAccount = newestAccount;
          } else if (isPrimary) {
            // On Account Transfer tab, keep the existing source if it's still valid.
            targetAccount = currentExists
              ? platformAccounts.find((acc) => acc.accountId === currentId)
              : platformAccounts[0];
          } else {
            // For the secondary slot, pick an account that isn't already used by the primary.
            targetAccount =
              platformAccounts.find((acc) => acc.accountId !== otherId) ||
              platformAccounts[0];
          }
        }

        // Apply the update if the account selection has changed.
        // Or if the account was deleted (handled by the reset logic above,
        // but currentExists check ensures we refresh stale data).
        if (targetAccount && targetAccount.accountId !== currentId) {
          hasChanges = true;
          nextState = {
            ...nextState,
            [role]: {
              ...nextState[role],
              [platform]: {
                ...currentAccount,
                ...(!currentExists ? initialPlatformState : {}),
                ...targetAccount,
                isSignedIn: true,
                isSigningIn: false,
              },
            },
          };
        }
      });
    });

    if (hasChanges) {
      setAuthState(nextState);
    }
  }, [accounts, pathname]);

  const oauth = useMemo(() => {
    const buildFacade = (role, platform, lib) => {
      const snapshot = authState[role][platform];

      const boundUpdate = (patch) => updateAuthState(role, platform, patch);

      return {
        ...snapshot,

        accounts: accounts?.[platform] || [],

        login: () => {
          lib.login(boundUpdate, onUnauthorized);
        },
        refresh: () => {
          lib.refresh(boundUpdate, onUnauthorized);
        },
        logout: () => {
          lib.logout(boundUpdate, onUnauthorized, snapshot.accountId);
        },
        reset: () => {
          boundUpdate({ ...initialPlatformState, accounts: [] });
        },
        switchAccount: (account) => {
          boundUpdate({ ...account, isSignedIn: true });
        },
      };
    };

    return {
      source: {
        apple: buildFacade('source', 'apple', apple),
        spotify: buildFacade('source', 'spotify', spotify),
        youtube: buildFacade('source', 'youtube', youtube),
      },
      target: {
        apple: buildFacade('target', 'apple', apple),
        spotify: buildFacade('target', 'spotify', spotify),
        youtube: buildFacade('target', 'youtube', youtube),
      },
    };
  }, [accounts, authState, onUnauthorized]);

  return (
    <OAuthContext.Provider value={oauth}>{children}</OAuthContext.Provider>
  );
};

export default OAuthProvider;
